import { Flex, HelpHover, Switch, Text, ToggleButton } from 'components'
import { startCase } from 'lodash-es'
import { PublicMoment } from 'types'
import { useEvent } from '../../../utils/eventUtils'
import { QrCodePreferences, useQrCodePreferences } from '../../../utils/preferences'
import PublicQrCode from './PublicQrCode'

interface Props {
  value: PublicMoment
  onChange: (moment: PublicMoment) => void
}

export default function ManagePublicMoment({ value, onChange }: Props) {
  const [preferences, setPreferences] = useQrCodePreferences()
  const { event } = useEvent()

  if (!event) return null

  function handleChange(body: Partial<PublicMoment>) {
    onChange({ ...value, ...body })
  }

  const { id, showTicket } = value
  const isDraft = !event?.moments.some(m => m.id === id)

  return (
    <Flex align="stretch" direction="column" gap={2}>
      <Flex align="center" mt={4} gap={2}>
        <Switch
          label="Swag Ticket"
          checked={showTicket ?? false}
          onChange={v => handleChange({ showTicket: v })}
        />
        <HelpHover text="Include a ticket for swag redemption after attendee completes registration." />
      </Flex>

      <Flex justify="center" position="relative">
        <PublicQrCode momentId={id} size={300} />
        {isDraft && (
          <Flex
            position="absolute"
            inset={0}
            justifyContent="center"
            alignItems="center"
            bg="rgba(0, 0, 0, 0.5)"
            borderRadius="md"
            backdropFilter="blur(7px)"
          >
            <Text color="white">Save Moments to activate QR Code</Text>
          </Flex>
        )}
      </Flex>

      <ToggleButton
        label="Style"
        value={preferences.dotStyle}
        buttonProps={{ flex: 1, justifyContent: 'center' }}
        options={dotStyles.map(s => ({
          value: s,
          label: (
            <Flex alignItems="center" direction="column" gap={1}>
              <Text textStyle="bodySmall">{startCase(s)}</Text>
            </Flex>
          ),
        }))}
        onChange={dotStyle => setPreferences(s => ({ ...s, dotStyle }))}
      />
      <ToggleButton
        label="Color"
        value={preferences.color}
        buttonProps={{ flex: 1, justifyContent: 'center' }}
        options={colors.map(c => ({
          value: c,
          label: (
            <Flex alignItems="center" direction="column" gap={1}>
              <Flex
                alignItems="center"
                backgroundColor={c}
                borderWidth="1px"
                borderColor="black"
                borderRadius="full"
                height={iconSize}
                justifyContent="center"
                width={iconSize}
              />
              <Text textStyle="bodySmall">{startCase(c)}</Text>
            </Flex>
          ),
        }))}
        onChange={color => setPreferences(s => ({ ...s, color }))}
      />
    </Flex>
  )
}

const iconSize = 22
const dotStyles = ['rounded', 'square', 'dots'] as QrCodePreferences['dotStyle'][]
const colors = ['black', 'white'] as const
